import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../../slices/authSlice';
import isoLanguages from '../../isoLanguages';
import '../../DashboardHome/brand-voice.css';
import './brandVoice.css';

// ============================================================
// Icons
// ============================================================
const ICN = {
    spark: (
        <svg viewBox="0 0 16 16" width="15" height="15" fill="none">
            <path d="M8 2l1.3 3.4L13 6.6 9.7 8 8 11.5 6.3 8 3 6.6l3.4-1.2L8 2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
        </svg>
    ),
    refresh: (
        <svg viewBox="0 0 14 14" width="13" height="13" fill="none">
            <path d="M11.5 3.5a5 5 0 10.8 4.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M11.5 1.5v2.5H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    x: (
        <svg viewBox="0 0 14 14" width="12" height="12" fill="none">
            <path d="M3.5 3.5l7 7M10.5 3.5l-7 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    ),
    plus: (
        <svg viewBox="0 0 14 14" width="13" height="13" fill="none">
            <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    ),
    check: (
        <svg viewBox="0 0 14 14" width="12" height="12" fill="none">
            <path d="M2.5 7l3 3 6-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    ban: (
        <svg viewBox="0 0 14 14" width="12" height="12" fill="none">
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M3.5 3.5l7 7" stroke="currentColor" strokeWidth="1.5" />
        </svg>
    ),
};

// ============================================================
// Constants
// ============================================================
const SUPPORTED_ADDRESS_LANGS = ['DE', 'FR', 'ES', 'IT', 'PT', 'NL', 'JA'];

const LANGS = [
    ['DE', 'German'],
    ['FR', 'French'],
    ['ES', 'Spanish'],
    ['JA', 'Japanese'],
];

const ADDRESS = {
    DE: [['formal', 'Sie · formal'], ['casual', 'du · informal']],
    FR: [['formal', 'vous'], ['casual', 'tu']],
    ES: [['formal', 'usted'], ['casual', 'tú']],
    IT: [['formal', 'Lei'], ['casual', 'tu']],
    PT: [['formal', 'você (formal)'], ['casual', 'tu']],
    NL: [['formal', 'u'], ['casual', 'jij']],
    JA: [['formal', 'desu/masu'], ['casual', 'plain form']],
};

const SAMPLES = {
    DE: [
        {
            src: 'Welcome back! Ready to pick up where you left off?',
            generic: 'Willkommen zurück! Bereit, dort weiterzumachen, wo Sie aufgehört haben?',
            formal: 'Schön, dass Sie wieder da sind. Möchten Sie dort weitermachen, wo Sie aufgehört haben?',
            casual: 'Schön, dass du wieder da bist. Lust, da weiterzumachen, wo du aufgehört hast?',
        },
        {
            src: 'Your free trial ends soon. Upgrade to keep your projects.',
            generic: 'Ihre kostenlose Testversion endet bald. Führen Sie ein Upgrade durch, um Ihre Projekte zu behalten.',
            formal: 'Ihre Testphase endet in Kürze — sichern Sie sich den vollen Zugang zu Ihren Projekten.',
            casual: 'Deine Testphase endet bald — sichere dir den vollen Zugang zu deinen Projekten.',
        },
        {
            src: 'Oops — something went wrong. Try again.',
            generic: 'Hoppla – etwas ist schiefgelaufen. Versuchen Sie es erneut.',
            formal: 'Da ist etwas schiefgelaufen. Bitte versuchen Sie es noch einmal.',
            casual: 'Da ist was schiefgelaufen. Versuch es einfach nochmal.',
        },
    ],
    FR: [
        {
            src: 'Welcome back! Ready to pick up where you left off?',
            generic: 'Bienvenue à nouveau ! Prêt à reprendre là où vous vous êtes arrêté ?',
            formal: 'Ravis de vous revoir. Souhaitez-vous reprendre là où vous vous êtes arrêté ?',
            casual: 'Content de te revoir ! Envie de reprendre là où tu t\'es arrêté ?',
        },
        {
            src: 'Your free trial ends soon. Upgrade to keep your projects.',
            generic: 'Votre essai gratuit se termine bientôt. Effectuez une mise à niveau pour conserver vos projets.',
            formal: 'Votre période d\'essai se termine bientôt — conservez l\'accès à tous vos projets.',
            casual: 'Ton essai se termine bientôt — garde l\'accès à tous tes projets.',
        },
        {
            src: 'Oops — something went wrong. Try again.',
            generic: 'Oups – une erreur s\'est produite. Veuillez réessayer.',
            formal: 'Une erreur s\'est produite. Veuillez réessayer.',
            casual: 'Aïe, ça n\'a pas marché. Réessaie.',
        },
    ],
    ES: [
        {
            src: 'Welcome back! Ready to pick up where you left off?',
            generic: '¡Bienvenido de nuevo! ¿Listo para continuar donde lo dejaste?',
            formal: 'Nos alegra verle de nuevo. ¿Desea continuar donde lo dejó?',
            casual: '¡Qué bueno verte! ¿Sigues donde lo dejaste?',
        },
    ],
    JA: [
        {
            src: 'Welcome back!',
            generic: 'おかえりなさい！',
            formal: 'お帰りなさいませ。',
            casual: 'おかえり。',
        },
    ],
};

// ============================================================
// Primitives — match the Settings page's set-block / set-field family
// ============================================================
const SetBlock = ({ title, desc, tag, flush, children, style }) => (
    <div className="set-block" style={style}>
        <div className={`set-block-head ${flush ? 'flush' : ''}`}>
            <h3>{title}{tag && <span className="tag">{tag}</span>}</h3>
            {desc && <p>{desc}</p>}
        </div>
        {children}
    </div>
);

const SetField = ({ label, desc, stack, top, children }) => (
    <div className={`set-field ${stack ? 'stack' : ''} ${top ? 'top' : ''}`}>
        <div className="set-field-label">
            <div className="lbl">{label}</div>
            {desc && <div className="desc">{desc}</div>}
        </div>
        <div className="set-field-control">{children}</div>
    </div>
);

const SetSelect = ({ value, onChange, options, minWidth }) => (
    <select
        className="set-input"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        style={minWidth ? { minWidth } : undefined}
    >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
);

const SetSegmented = ({ value, onChange, options }) => (
    <div className="bv-seg">
        {options.map((o) => (
            <button
                type="button"
                key={o.value}
                className={value === o.value ? 'on' : ''}
                onClick={() => onChange(o.value)}
            >
                {o.label}
            </button>
        ))}
    </div>
);

// ============================================================
// Sub-components
// ============================================================
const ToneSlider = ({ label, left, right, value, onChange, hint }) => (
    <div className="bv-tone">
        <div className="bv-tone-h">
            <span className="lbl">{label}</span>
            <span className="val">{value}</span>
        </div>
        {hint && <div className="bv-tone-hint">{hint}</div>}
        <div className="bv-tone-row">
            <span className="end">{left}</span>
            <input
                type="range"
                className="set-slider"
                min={0}
                max={100}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
            />
            <span className="end">{right}</span>
        </div>
    </div>
);

const ChipInput = ({ items, onAdd, onRemove, placeholder }) => {
    const [v, setV] = useState('');
    const add = () => {
        const x = v.trim();
        if (x && !items.includes(x)) onAdd(x);
        setV('');
    };
    return (
        <div className="bv-chips">
            {items.map((it) => (
                <span key={it} className="bv-chip">
                    {it}
                    <button type="button" onClick={() => onRemove(it)} aria-label={`Remove ${it}`}>{ICN.x}</button>
                </span>
            ))}
            <input
                className="bv-chip-input"
                value={v}
                placeholder={placeholder}
                onChange={(e) => setV(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            />
        </div>
    );
};

const TryVoice = ({ activeLang, voice, token, languages }) => {
    const [idx, setIdx] = useState(0);
    const samples = SAMPLES[activeLang] || SAMPLES.DE;
    const [live, setLive] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);
    const [autoOn, setAutoOn] = useState(false);

    useEffect(() => { setIdx(0); setLive(null); setErr(null); }, [activeLang]);
    useEffect(() => { setLive(null); }, [idx]);

    const s = samples[idx] || samples[0];
    const addr = (voice.address || {})[activeLang] || 'formal';
    const fallbackOnBrand = addr === 'casual' ? s.casual : s.formal;
    const langName = (languages.find((l) => l[0] === activeLang)
        || LANGS.find((l) => l[0] === activeLang) || [])[1] || activeLang;
    const addrRow = (ADDRESS[activeLang] || ADDRESS.DE).find((a) => a[0] === addr);
    const addrLabel = addrRow ? addrRow[1] : addr;
    const warm = (voice.tone?.warmth ?? 50) >= 55;

    const regenerate = async ({ enableAuto = true } = {}) => {
        setLoading(true);
        setErr(null);
        try {
            const { _id, userId, __v, updatedAt, ...payload } = voice;
            const r = await axios.post(
                '/api/brand-voice/preview',
                { source: s.src, targetLang: activeLang, voice: payload },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            setLive(r.data || null);
            if (enableAuto) setAutoOn(true);
        } catch (e) {
            setErr(e.response?.data?.msg || 'Could not generate live preview.');
        } finally {
            setLoading(false);
        }
    };

    const profileSignature = JSON.stringify({
        s: voice.summary || '',
        t: voice.tone || {},
        a: (voice.address || {})[activeLang] || 'formal',
        r: voice.reading,
        l: voice.length,
        d: voice.doNotTranslate || [],
        f: voice.forbidden || [],
        p: voice.preferred || [],
        e: voice.examples || [],
        src: s.src,
        lang: activeLang,
    });
    useEffect(() => {
        if (!autoOn) return undefined;
        const t = setTimeout(() => { regenerate({ enableAuto: false }); }, 800);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileSignature, autoOn]);

    const genericText = live?.generic || s.generic;
    const onBrandText = live?.onBrand || fallbackOnBrand;

    return (
        <div className="bv-try">
            <div className="bv-try-h">
                <span className="bv-try-ic">{ICN.spark}</span>
                <div>
                    <b>Try the voice</b>
                    <span>
                        {autoOn
                            ? (loading ? 'Updating live preview…' : 'Live preview · auto-updates as you edit.')
                            : 'See how Rush AI applies your profile — compared to a generic engine.'}
                    </span>
                </div>
                <div className="grow" />
                {autoOn && (
                    <label
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            fontSize: 12, color: 'var(--ink-3)', marginRight: 10, cursor: 'pointer',
                        }}
                        title="Stop auto-regenerating on every edit."
                    >
                        <input type="checkbox" checked={autoOn} onChange={(e) => setAutoOn(e.target.checked)} />
                        Auto-update
                    </label>
                )}
                <button type="button" className="set-btn sm" onClick={() => regenerate()} disabled={loading}>
                    {ICN.refresh} {loading ? 'Generating…' : (autoOn ? 'Regenerate now' : 'Regenerate')}
                </button>
            </div>

            <div className="bv-try-samples">
                {samples.map((sm, i) => (
                    <button
                        type="button"
                        key={i}
                        className={`bv-sample ${i === idx ? 'on' : ''}`}
                        onClick={() => setIdx(i)}
                    >
                        {sm.src}
                    </button>
                ))}
            </div>

            {err && (
                <div style={{ color: 'crimson', fontSize: 12.5, padding: '6px 18px' }}>{err}</div>
            )}

            <div className="bv-try-out">
                <div className="bv-out generic">
                    <div className="bv-out-h"><span className="tag">Generic engine</span></div>
                    <div className="bv-out-t">{genericText}</div>
                </div>
                <div className="bv-out brand">
                    <div className="bv-out-h">
                        <span className="tag accent">{ICN.spark} On-brand · Rush AI</span>
                    </div>
                    <div className="bv-out-t">{onBrandText}</div>
                    <div className="bv-out-chips">
                        <span className="ac">{addrLabel}</span>
                        <span className="ac">{warm ? 'Warm tone' : 'Neutral tone'}</span>
                        <span className="ac">{langName} voice profile</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const LangBar = ({ active, onChange, languages, addable, onAdd }) => {
    const [picking, setPicking] = useState(false);
    return (
        <div className="bv-langbar" style={{ position: 'relative' }}>
            <span className="bv-langbar-l">Voice profile for</span>
            {languages.map(([code, name]) => (
                <button
                    type="button"
                    key={code}
                    className={`bv-lang ${active === code ? 'on' : ''}`}
                    onClick={() => onChange(code)}
                >
                    <span className="code">{code}</span>{name}
                </button>
            ))}
            {addable.length > 0 && (
                <>
                    <button type="button" className="bv-lang add" onClick={() => setPicking((p) => !p)}>
                        {ICN.plus} Add language
                    </button>
                    {picking && (
                        <div
                            style={{
                                position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                                background: 'var(--bg-card)', border: '1px solid var(--line)',
                                borderRadius: 10, boxShadow: '0 6px 24px rgba(0,0,0,0.08)',
                                padding: 6, zIndex: 20, minWidth: 180,
                            }}
                        >
                            {addable.map(([code, name]) => (
                                <button
                                    type="button"
                                    key={code}
                                    onClick={() => { onAdd(code); setPicking(false); }}
                                    style={{
                                        display: 'block', width: '100%', textAlign: 'left',
                                        padding: '8px 10px', border: 'none', background: 'transparent',
                                        cursor: 'pointer', borderRadius: 6, color: 'var(--ink)', fontSize: 13,
                                    }}
                                >
                                    <span style={{ fontWeight: 600, marginRight: 8 }}>{code}</span>{name}
                                </button>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

// ============================================================
// Rule rows — display-style list for Forbidden / Preferred.
// Editing existing entries: an inline pencil opens a small editor row.
// ============================================================
const RuleRow = ({ kind, primary, secondary, onRemove }) => (
    <div className="bv-rule">
        <span className={`bv-rule-ic ${kind === 'ban' ? 'ban' : 'ok'}`}>
            {kind === 'ban' ? ICN.ban : ICN.check}
        </span>
        <div className="bv-rule-body">
            <b>{primary}</b>
            {secondary && <span>{secondary}</span>}
        </div>
        <button type="button" className="bv-rule-x" onClick={onRemove} aria-label="Remove">{ICN.x}</button>
    </div>
);

const AddRuleInline = ({ placeholders, onAdd, addLabel }) => {
    const [open, setOpen] = useState(false);
    const [a, setA] = useState('');
    const [b, setB] = useState('');
    if (!open) {
        return (
            <div className="bv-rule add">
                <button type="button" className="set-btn sm" onClick={() => setOpen(true)}>
                    {ICN.plus} {addLabel}
                </button>
            </div>
        );
    }
    const submit = () => {
        if (!a.trim()) return;
        onAdd(a.trim(), b.trim());
        setA(''); setB(''); setOpen(false);
    };
    const cancel = () => { setA(''); setB(''); setOpen(false); };
    const inputStyle = {
        flex: 1, minWidth: 0, height: 32, padding: '0 10px',
        border: '1px solid var(--line)', borderRadius: 8,
        background: 'var(--bg)', color: 'var(--ink)', fontSize: 13, fontFamily: 'inherit', outline: 'none',
    };
    return (
        <div className="bv-rule add" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
                style={inputStyle}
                value={a}
                placeholder={placeholders[0]}
                onChange={(e) => setA(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') cancel(); }}
                autoFocus
            />
            <input
                style={inputStyle}
                value={b}
                placeholder={placeholders[1]}
                onChange={(e) => setB(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') cancel(); }}
            />
            <button type="button" className="set-btn sm primary" onClick={submit}>Add</button>
            <button type="button" className="set-btn sm" onClick={cancel}>Cancel</button>
        </div>
    );
};

// ============================================================
// Examples — design's bv-example layout with inline edit
// ============================================================
const ExamplesEditor = ({ items, onChange }) => {
    const updateAt = (i, k, val) => {
        const next = items.map((it, idx) => (idx === i ? { ...it, [k]: val } : it));
        onChange(next);
    };
    const removeAt = (i) => onChange(items.filter((_, idx) => idx !== i));

    const inputBase = {
        border: 'none', background: 'transparent', outline: 'none',
        color: 'inherit', fontFamily: 'inherit', fontSize: 13, lineHeight: 1.5, flex: 1, padding: 0,
    };

    return (
        <div className="bv-pad bv-examples">
            {items.map((ex, i) => (
                <div className="bv-example" key={i} style={{ position: 'relative' }}>
                    <input
                        className="bv-ex-src"
                        value={ex.source || ex.src || ''}
                        onChange={(e) => updateAt(i, 'source', e.target.value)}
                        placeholder="Source phrase"
                        style={{ ...inputBase, display: 'block', width: '100%',
                            padding: '11px 14px', fontWeight: 500,
                            background: 'var(--bg-soft)', borderBottom: '1px solid var(--line)' }}
                    />
                    <div className="bv-ex-row good">
                        <span className="bv-ex-k">{ICN.check} On-brand</span>
                        <input
                            value={ex.good || ''}
                            onChange={(e) => updateAt(i, 'good', e.target.value)}
                            placeholder="On-brand rendering"
                            style={inputBase}
                        />
                    </div>
                    <div className="bv-ex-row avoid">
                        <span className="bv-ex-k">{ICN.ban} Avoid</span>
                        <input
                            value={ex.avoid || ''}
                            onChange={(e) => updateAt(i, 'avoid', e.target.value)}
                            placeholder="What not to do"
                            style={{ ...inputBase, textDecoration: 'line-through',
                                textDecorationColor: 'var(--line-2)' }}
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => removeAt(i)}
                        aria-label="Remove example"
                        style={{
                            position: 'absolute', top: 8, right: 8,
                            width: 24, height: 24, borderRadius: 6, border: 0,
                            background: 'transparent', color: 'var(--ink-4)',
                            cursor: 'pointer', display: 'grid', placeItems: 'center',
                        }}
                    >
                        {ICN.x}
                    </button>
                </div>
            ))}
            <button
                type="button"
                className="bv-add-ex"
                onClick={() => onChange([...items, { source: 'New source phrase', good: 'On-brand translation', avoid: 'What not to do' }])}
            >
                {ICN.plus} Add example pair
            </button>
        </div>
    );
};

// ============================================================
// First-run detection — true when the voice has never been touched
// (everything still at the server defaults). Drives whether the guided
// setup auto-launches.
// ============================================================
const GUIDE_DISMISS_KEY = 'rush.brandVoice.guideDismissed';

const isPristineVoice = (v) => {
    if (!v) return false;
    const t = v.tone || {};
    const toneAtDefault = ['formality', 'warmth', 'playfulness', 'energy']
        .every((k) => (t[k] ?? 50) === 50);
    const addrAtDefault = Object.values(v.address || {}).every((x) => !x || x === 'formal');
    return (
        !(v.summary || '').trim()
        && toneAtDefault
        && addrAtDefault
        && (v.reading || 'standard') === 'standard'
        && (v.length || 'balanced') === 'balanced'
        && !(v.doNotTranslate || []).length
        && !(v.forbidden || []).length
        && !(v.preferred || []).length
        && !(v.examples || []).length
    );
};

// ============================================================
// Guided setup — a step-by-step wizard over the same `voice` state and
// handlers used by the full editor. Each step reuses the editor's own
// primitives so the two views stay perfectly in sync.
// ============================================================
// A short "what to do here" callout shown at the top of each wizard step.
const WizTip = ({ children }) => (
    <div className="bv-wiz-tip">
        <span className="bv-wiz-tip-ic">{ICN.spark}</span>
        <p>{children}</p>
    </div>
);

const GUIDE_SUMMARY_IDEAS = [
    'Warm and human, never corporate. We write like a helpful colleague, not a manual.',
    'Confident and concise. Short sentences, plain words, no filler.',
    'Friendly and playful, but always clear — we explain before we delight.',
];

const GuideFlow = ({
    voice, update, updateTone, updateAddress,
    token, langTabs, addableLangs, addLangTab,
    activeLang, setActiveLang, nameOf,
    onExit, onFinish, saving,
}) => {
    const [step, setStep] = useState(0);

    const steps = [
        { key: 'langs', label: 'Languages', title: 'Which languages do you ship in?', sub: 'Add every language you publish in. You’ll tune the voice for each one — you can always add more later.' },
        { key: 'tone', label: 'Tone & personality', title: 'How should your brand sound?', sub: 'Set where your brand sits on each axis. Rush AI leans every draft toward these.' },
        { key: 'address', label: 'Reader & register', title: 'How do you address the reader?', sub: 'Form of address, reading level, and sentence rhythm — tuned per language.' },
        { key: 'summary', label: 'Voice summary', title: 'Describe your voice in a sentence', sub: 'This brief is passed verbatim to the model — the single most direct way to nudge style.' },
        { key: 'keep', label: 'Names to keep', title: 'Anything that must stay verbatim?', sub: 'Product names, brands, and reserved terms kept untranslated in every language.' },
        { key: 'words', label: 'Words to avoid & prefer', title: 'Words to avoid — and to reach for', sub: 'Ban off-brand terms and lock in your approved phrasings.' },
        { key: 'examples', label: 'Examples', title: 'Teach the AI by example', sub: 'Optional, but powerful. A few on-brand pairs teach your style faster than any rule.' },
        { key: 'try', label: 'Try it & finish', title: 'Hear your voice, then save', sub: 'See your profile applied live. When it sounds right, save to apply it everywhere.' },
    ];
    const total = steps.length;
    const cur = steps[step];
    const last = step === total - 1;

    const next = () => setStep((s) => Math.min(total - 1, s + 1));
    const back = () => setStep((s) => Math.max(0, s - 1));

    const activeLangName = nameOf(activeLang);
    const addrOpts = (ADDRESS[activeLang] || ADDRESS.DE).map((a) => ({ value: a[0], label: a[1] }));
    const currentAddr = (voice.address || {})[activeLang] || 'formal';

    let body = null;
    if (cur.key === 'langs') {
        body = (
            <>
                <WizTip>
                    Click <b>Add language</b> for each language you publish in, then pick one to tune first.
                    Tone, summary, and word rules apply across all of them; only the form of address is set
                    per language (next step). You can come back and add more anytime.
                </WizTip>
                <LangBar
                    active={activeLang}
                    onChange={setActiveLang}
                    languages={langTabs}
                    addable={addableLangs}
                    onAdd={addLangTab}
                />
                <p className="bv-wiz-note">
                    The highlighted language is the one you’re tuning right now.
                </p>
            </>
        );
    } else if (cur.key === 'tone') {
        body = (
<>
                <WizTip>
                    Drag each slider to where your brand naturally sits. The midpoint (50) is neutral —
                    move toward either end only as far as feels true. There are no wrong answers; you can
                    hear the effect on the final step and adjust.
                </WizTip>
                <div className="bv-wiz-card">
                    <div className="bv-tones">
                        <ToneSlider label="Formality" left="Casual" right="Formal"
                            hint="How buttoned-up the language feels — relaxed and conversational, or polished and professional."
                            value={voice.tone?.formality ?? 50}
                            onChange={(v) => updateTone('formality', v)} />
                        <ToneSlider label="Warmth" left="Neutral" right="Warm"
                            hint="How personal and friendly the copy reads, versus neutral and matter-of-fact."
                            value={voice.tone?.warmth ?? 50}
                            onChange={(v) => updateTone('warmth', v)} />
                        <ToneSlider label="Playfulness" left="Serious" right="Playful"
                            hint="Room for wit, wordplay, and lightness — versus a straight, serious register."
                            value={voice.tone?.playfulness ?? 50}
                            onChange={(v) => updateTone('playfulness', v)} />
                        <ToneSlider label="Energy" left="Calm" right="Energetic"
                            hint="The pace and drive of the writing — measured and steady, or punchy and urgent."
                            value={voice.tone?.energy ?? 50}
                            onChange={(v) => updateTone('energy', v)} />
                    </div>
                </div>
            </>
        );
    } else if (cur.key === 'address') {
        body = (
            <>
                <WizTip>
                    <b>Form of address</b> is set per language — switch languages above to set each one.
                    <b> Reading level</b> and <b>sentence length</b> apply to all languages. Pick what
                    matches how you’d speak to your reader.
                </WizTip>
                <LangBar
                    active={activeLang}
                    onChange={setActiveLang}
                    languages={langTabs}
                    addable={addableLangs}
                    onAdd={addLangTab}
                />
                <div className="bv-wiz-card bv-wiz-fields">
                    <SetField label="Form of address" desc={`Which pronoun to use for "you" in ${activeLangName} — formal keeps a respectful distance, casual is warmer and more direct.`}>
                        <SetSegmented
                            value={currentAddr}
                            onChange={(v) => updateAddress(activeLang, v)}
                            options={addrOpts}
                        />
                    </SetField>
                    <SetField label="Reading level" desc="How complex the vocabulary and sentences should be. Use a CEFR level (A1–C2) to target learners, or keep Standard to match the source.">
                        <SetSelect
                            value={voice.reading || 'standard'}
                            onChange={(v) => update({ reading: v })}
                            minWidth={180}
                            options={[
                                { value: 'simple', label: 'Plain & simple' },
                                { value: 'standard', label: 'Standard' },
                                { value: 'A1', label: 'CEFR A1 — Beginner' },
                                { value: 'A2', label: 'CEFR A2 — Elementary' },
                                { value: 'B1', label: 'CEFR B1 — Intermediate' },
                                { value: 'B2', label: 'CEFR B2 — Upper-intermediate' },
                                { value: 'C1', label: 'CEFR C1 — Advanced' },
                                { value: 'C2', label: 'CEFR C2 — Proficient' },
                                { value: 'technical', label: 'Expert / technical' },
                            ]}
                        />
                    </SetField>
                    <SetField label="Sentence length" desc="The rhythm of the copy — Short is punchy and scannable, Flowing allows longer, more considered sentences.">
                        <SetSegmented
                            value={voice.length || 'balanced'}
                            onChange={(v) => update({ length: v })}
                            options={[
                                { value: 'terse', label: 'Short' },
                                { value: 'balanced', label: 'Balanced' },
                                { value: 'expansive', label: 'Flowing' },
                            ]}
                        />
                    </SetField>
                </div>
            </>
        );
    } else if (cur.key === 'summary') {
        body = (
            <>
                <WizTip>
                    Write one or two sentences, as if briefing a new copywriter, on how your brand should
                    sound. This is passed straight to the AI, so be specific — “warm but never chatty”
                    beats “professional.” Tap a starter below to fill it in, then edit.
                </WizTip>
                <div className="bv-wiz-card bv-pad">
                <textarea
                    value={voice.summary || ''}
                    onChange={(e) => update({ summary: e.target.value })}
                    placeholder="Describe how your brand should sound in one or two sentences…"
                    style={{
                        width: '100%', minHeight: 90,
                        border: '1px solid var(--line)', borderRadius: 10, padding: 12,
                        background: 'var(--bg)', color: 'var(--ink)',
                        fontFamily: 'inherit', fontSize: 13.5, outline: 'none', resize: 'vertical',
                    }}
                />
                <div className="bv-wiz-ideas">
                    <span className="bv-wiz-ideas-l">Need a starting point?</span>
                    {GUIDE_SUMMARY_IDEAS.map((idea, i) => (
                        <button
                            type="button"
                            key={i}
                            className="bv-wiz-idea"
                            onClick={() => update({ summary: idea })}
                        >
                            {idea}
                        </button>
                    ))}
                </div>
                </div>
            </>
        );
    } else if (cur.key === 'keep') {
        body = (
            <>
                <WizTip>
                    Add any product names, brand names, or technical terms that must appear exactly as
                    written in every language — they’ll never be translated. Type a term and press Enter.
                    Nothing to lock down? Just skip ahead.
                </WizTip>
                <div className="bv-wiz-card bv-pad">
                <ChipInput
                    items={voice.doNotTranslate || []}
                    placeholder="Add a term and press Enter…"
                    onAdd={(x) => update({ doNotTranslate: [...(voice.doNotTranslate || []), x] })}
                    onRemove={(x) => update({ doNotTranslate: (voice.doNotTranslate || []).filter((i) => i !== x) })}
                />
                <p className="bv-wiz-note">
                    Honored by every engine, including DeepL and Google.
                </p>
                </div>
            </>
        );
    } else if (cur.key === 'words') {
        body = (
            <>
                <WizTip>
                    <b>Forbidden</b> words are ones the AI must never use — add the term and why (e.g. avoid
                    “cheap” → use “affordable”). <b>Preferred</b> pairs swap a phrasing you dislike for your
                    approved one. Both are optional, but a few of each sharpens every draft.
                </WizTip>
                <div className="bv-grid">
                <SetBlock title="Avoid these" tag="Forbidden" flush
                    desc="Words the AI will never use, with the reason."
                >
                    {(voice.forbidden || []).map((f, i) => (
                        <RuleRow
                            key={i}
                            kind="ban"
                            primary={f.term || f.t || ''}
                            secondary={f.reason || f.r || ''}
                            onRemove={() => update({ forbidden: (voice.forbidden || []).filter((_, j) => j !== i) })}
                        />
                    ))}
                    <AddRuleInline
                        placeholders={['Term to avoid', 'Why / what to use instead']}
                        addLabel="Add forbidden term"
                        onAdd={(a, b) => update({ forbidden: [...(voice.forbidden || []), { term: a, reason: b }] })}
                    />
                </SetBlock>

                <SetBlock title="Prefer these" tag="Preferred" flush
                    desc="Approved phrasings the AI should reach for first."
                >
                    {(voice.preferred || []).map((p, i) => (
                        <RuleRow
                            key={i}
                            kind="ok"
                            primary={(
                                <>
                                    {p.avoid || p.a || ''}
                                    <span className="arr">→</span>
                                    {p.use || p.b || ''}
                                </>
                            )}
                            onRemove={() => update({ preferred: (voice.preferred || []).filter((_, j) => j !== i) })}
                        />
                    ))}
                    <AddRuleInline
                        placeholders={['Avoid', 'Prefer']}
                        addLabel="Add preferred term"
                        onAdd={(a, b) => update({ preferred: [...(voice.preferred || []), { avoid: a, use: b }] })}
                    />
                </SetBlock>
                </div>
            </>
        );
    } else if (cur.key === 'examples') {
        body = (
            <>
                <WizTip>
                    Optional, but the fastest way to teach your style. For each example, give a source
                    phrase, the <b>on-brand</b> way you’d translate it, and (optionally) a version to
                    <b> avoid</b>. Even two or three pairs make a big difference. Skip if you’re short on time.
                </WizTip>
                <div className="set-block" style={{ marginBottom: 0 }}>
                    <ExamplesEditor
                        items={voice.examples || []}
                        onChange={(nextItems) => update({ examples: nextItems })}
                    />
                </div>
            </>
        );
    } else if (cur.key === 'try') {
        body = (
            <>
                <WizTip>
                    This is your voice in action. Pick a sample sentence and compare a generic engine
                    against your profile. Not quite right? Jump back to any step using the list on the
                    left, tweak, and try again. When it sounds like you, hit <b>Save &amp; finish</b>.
                </WizTip>
                <TryVoice activeLang={activeLang} voice={voice} token={token} languages={langTabs} />
                <p className="bv-wiz-note">
                    Saving applies this voice to every draft across your workspace. You can edit it anytime.
                </p>
            </>
        );
    }

    return (
        <div className="set-body" style={{ maxWidth: '100%' }}>
            <div className="bv-wiz">
                <aside className="bv-wiz-rail">
                    <div className="bv-wiz-rail-h">
                        <b>Set up your brand voice</b>
                        <span>{total} quick steps</span>
                    </div>
                    {steps.map((s, i) => (
                        <button
                            type="button"
                            key={s.key}
                            className={`bv-wiz-rstep ${i === step ? 'on' : ''} ${i < step ? 'done' : ''}`}
                            onClick={() => setStep(i)}
                        >
                            <span className="n">{i < step ? ICN.check : i + 1}</span>
                            <span className="t">{s.label}</span>
                        </button>
                    ))}
                    <button type="button" className="bv-wiz-exit" onClick={onExit}>
                        Switch to full editor
                    </button>
                </aside>

                <div className="bv-wiz-main">
                    <div className="bv-wiz-head">
                        <span className="bv-wiz-count">Step {step + 1} of {total}</span>
                        <h2>{cur.title}</h2>
                        <p>{cur.sub}</p>
                    </div>

                    <div className="bv-wiz-body">{body}</div>

                    <div className="bv-wiz-foot">
                        <button type="button" className="set-btn" onClick={back} disabled={step === 0}>
                            Back
                        </button>
                        <div className="grow" style={{ flex: 1 }} />
                        {!last ? (
                            <button type="button" className="set-btn primary" onClick={next}>
                                Continue
                            </button>
                        ) : (
                            <button type="button" className="set-btn primary" onClick={onFinish} disabled={saving}>
                                {saving ? 'Saving…' : 'Save & finish'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============================================================
// Upgrade teaser — shown when the workspace plan doesn't include Brand Voice.
// Driven by the backend's FEATURE_LOCKED 403 (gate keys on the billing user),
// so seated team members on an entitled workspace never see this.
// ============================================================
const LOCK_PREVIEWS = [
    ['Tone & personality', 'Dial in formality, warmth, playfulness, and energy.'],
    ['Per-language register', 'Set formal vs. casual address for each language.'],
    ['Forbidden & preferred', 'Ban off-brand words; lock in approved phrasings.'],
    ['Examples that train the AI', 'Teach your style with a few on-brand pairs.'],
];

const BrandVoiceLocked = ({ requiredTier = 'Growth', onUpgrade }) => (
    <>
        <div className="rs-page-title-row">
            <h1 className="rs-page-title">Brand Voice</h1>
        </div>
        <p className="rs-page-sub">
            Define how your brand sounds in every language, so Rush AI keeps your voice
            consistent across every word you ship.
        </p>

        <div className="bv-lock">
            <span className="bv-lock-badge">{requiredTier} feature</span>
            <span className="bv-lock-ic">{ICN.spark}</span>
            <h2>Unlock Brand Voice with {requiredTier}</h2>
            <p>
                Brand Voice teaches the AI your tone, register, and approved wording, then applies
                it to every draft. It’s part of the {requiredTier} plan, alongside premium AI, analytics,
                and layout-risk checks.
            </p>
            <div className="bv-lock-grid">
                {LOCK_PREVIEWS.map(([t, d]) => (
                    <div className="bv-lock-card" key={t}>
                        <b>{t}</b>
                        <span>{d}</span>
                    </div>
                ))}
            </div>
            <button type="button" className="btn-p" onClick={onUpgrade}>
                View plans &amp; upgrade
            </button>
        </div>
    </>
);

// ============================================================
// Page
// ============================================================
const BrandVoice = () => {
    const token = useSelector((s) => s.auth.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [voice, setVoice] = useState(null);
    const [original, setOriginal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [activeLang, setActiveLang] = useState('DE');
    const [activeLangs, setActiveLangs] = useState([]);
    const [mode, setMode] = useState('editor'); // 'editor' | 'guide'
    const [locked, setLocked] = useState(null); // { requiredTier } when plan-gated

    useEffect(() => {
        if (!token) return;
        const headers = { Authorization: `Bearer ${token}` };
        Promise.all([
            axios.get('/api/brand-voice', { headers }),
            axios.get('/api/brand-voice/used-languages', { headers }).catch(() => ({ data: { used: [] } })),
        ])
            .then(([voiceRes, usedRes]) => {
                setVoice(voiceRes.data);
                setOriginal(JSON.stringify(voiceRes.data));
                const saved = Object.keys((voiceRes.data && voiceRes.data.address) || {})
                    .map((c) => c.toUpperCase())
                    .filter((c) => SUPPORTED_ADDRESS_LANGS.includes(c));
                const used = (usedRes.data?.used || [])
                    .map((c) => String(c).toUpperCase())
                    .filter((c) => SUPPORTED_ADDRESS_LANGS.includes(c));
                const merged = [];
                used.forEach((c) => { if (!merged.includes(c)) merged.push(c); });
                saved.forEach((c) => { if (!merged.includes(c)) merged.push(c); });
                if (!merged.length) merged.push('DE');
                setActiveLangs(merged);
                if (!merged.includes(activeLang)) setActiveLang(merged[0]);
                // First run: if the voice has never been configured and the
                // guide hasn't been dismissed, open straight into guided setup.
                let dismissed = false;
                try { dismissed = window.localStorage.getItem(GUIDE_DISMISS_KEY) === '1'; } catch (e) { /* noop */ }
                if (isPristineVoice(voiceRes.data) && !dismissed) setMode('guide');
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else if (err.response && err.response.status === 403
                    && err.response.data?.code === 'FEATURE_LOCKED') {
                    // Plan-gated workspace — show the upgrade teaser instead.
                    setLocked({ requiredTier: err.response.data.requiredTier || 'Growth' });
                    setLoading(false);
                } else {
                    setError('Failed to load brand voice.');
                    setLoading(false);
                }
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, dispatch, navigate]);

    const dirty = voice && original && JSON.stringify(voice) !== original;

    const update = (patch) => setVoice((v) => ({ ...v, ...patch }));
    const updateTone = (k, val) => setVoice((v) => ({ ...v, tone: { ...v.tone, [k]: val } }));
    const updateAddress = (lang, val) => setVoice((v) => ({ ...v, address: { ...(v.address || {}), [lang]: val } }));

    const save = async () => {
        setSaving(true);
        try {
            const { _id, userId, __v, updatedAt, ...payload } = voice;
            await axios.put('/api/brand-voice', payload, { headers: { Authorization: `Bearer ${token}` } });
            setOriginal(JSON.stringify(voice));
        } catch (e) {
            setError('Failed to save.');
        } finally {
            setSaving(false);
        }
    };

    const discard = () => {
        if (!original) return;
        try { setVoice(JSON.parse(original)); } catch (e) { /* noop */ }
    };

    const rememberGuideDismissed = () => {
        try { window.localStorage.setItem(GUIDE_DISMISS_KEY, '1'); } catch (e) { /* noop */ }
    };

    // Leave the guide for the full editor without saving — keeps any edits
    // already made in the wizard, since both views share the same `voice`.
    const exitGuide = () => {
        rememberGuideDismissed();
        setMode('editor');
    };

    // Final step of the guide: persist, then drop into the full editor.
    const finishGuide = async () => {
        await save();
        rememberGuideDismissed();
        setMode('editor');
    };

    const languageOptions = useMemo(() => {
        const codes = new Set(SUPPORTED_ADDRESS_LANGS);
        return isoLanguages
            .filter((l) => codes.has(l.code.toUpperCase()))
            .map((l) => ({ code: l.code.toUpperCase(), name: l.name }));
    }, []);

    const nameOf = (code) => (languageOptions.find((o) => o.code === code) || {}).name
        || (LANGS.find((l) => l[0] === code) || [])[1]
        || code;
    const langTabs = activeLangs.map((c) => [c, nameOf(c)]);
    const addableLangs = SUPPORTED_ADDRESS_LANGS
        .filter((c) => !activeLangs.includes(c))
        .map((c) => [c, nameOf(c)]);

    const addLangTab = (code) => {
        setActiveLangs((cur) => (cur.includes(code) ? cur : [...cur, code]));
        if (voice && !((voice.address || {})[code])) updateAddress(code, 'formal');
        setActiveLang(code);
    };

    if (loading) return <div style={{ padding: 40, color: 'var(--ink-3)' }}>Loading brand voice…</div>;
    if (locked) return <BrandVoiceLocked requiredTier={locked.requiredTier} onUpgrade={() => navigate('/dashboard/pricing')} />;
    if (error) return <div style={{ padding: 40, color: 'crimson' }}>{error}</div>;
    if (!voice) return null;

    const activeLangName = nameOf(activeLang);
    const addrOpts = (ADDRESS[activeLang] || ADDRESS.DE).map((a) => ({ value: a[0], label: a[1] }));
    const currentAddr = (voice.address || {})[activeLang] || 'formal';

    return (
        <>
            <div className="rs-page-title-row">
                <h1 className="rs-page-title">Brand Voice</h1>
                <div className="rs-page-actions">
                    {mode === 'editor' ? (
                        <>
                            <button
                                type="button"
                                className="btn-q"
                                onClick={() => { setMode('guide'); }}
                            >
                                Setup guide
                            </button>
                            <button
                                type="button"
                                className="btn-q"
                                onClick={discard}
                                disabled={!dirty}
                                style={!dirty ? { opacity: 0.5, cursor: 'not-allowed' } : null}
                            >
                                Discard
                            </button>
                            <button
                                type="button"
                                className="btn-p"
                                onClick={save}
                                disabled={!dirty || saving}
                                style={(!dirty || saving) ? { opacity: 0.5, cursor: 'not-allowed' } : null}
                            >
                                {saving ? 'Saving…' : 'Save changes'}
                            </button>
                        </>
                    ) : (
                        <button type="button" className="btn-q" onClick={exitGuide}>
                            Skip to full editor
                        </button>
                    )}
                </div>
            </div>
            <p className="rs-page-sub">
                {mode === 'guide'
                    ? 'Let’s set up your brand voice together. Work through each step — your changes save when you finish.'
                    : 'Define how your brand sounds in each language. Rush AI applies this profile to every draft, so brand voice stays consistent across every word you ship.'}
            </p>

            {mode === 'guide' ? (
                <GuideFlow
                    voice={voice}
                    update={update}
                    updateTone={updateTone}
                    updateAddress={updateAddress}
                    token={token}
                    langTabs={langTabs}
                    addableLangs={addableLangs}
                    addLangTab={addLangTab}
                    activeLang={activeLang}
                    setActiveLang={setActiveLang}
                    nameOf={nameOf}
                    onExit={exitGuide}
                    onFinish={finishGuide}
                    saving={saving}
                />
            ) : (
            <>
            <LangBar
                active={activeLang}
                onChange={setActiveLang}
                languages={langTabs}
                addable={addableLangs}
                onAdd={addLangTab}
            />

            <TryVoice activeLang={activeLang} voice={voice} token={token} languages={langTabs} />

            <div className="set-body" style={{ maxWidth: '100%' }}>
                <div className="bv-grid">
                    <SetBlock
                        title="Tone & personality"
                        desc="Where your brand sits on each axis. The AI leans drafts toward these."
                    >
                        <div className="bv-tones">
                            <ToneSlider label="Formality" left="Casual" right="Formal"
                                value={voice.tone?.formality ?? 50}
                                onChange={(v) => updateTone('formality', v)} />
                            <ToneSlider label="Warmth" left="Neutral" right="Warm"
                                value={voice.tone?.warmth ?? 50}
                                onChange={(v) => updateTone('warmth', v)} />
                            <ToneSlider label="Playfulness" left="Serious" right="Playful"
                                value={voice.tone?.playfulness ?? 50}
                                onChange={(v) => updateTone('playfulness', v)} />
                            <ToneSlider label="Energy" left="Calm" right="Energetic"
                                value={voice.tone?.energy ?? 50}
                                onChange={(v) => updateTone('energy', v)} />
                        </div>
                    </SetBlock>

                    <SetBlock
                        title="Formality & form of address"
                        desc={`How we address the reader in ${activeLangName}.`}
                    >
                        <SetField label="Form of address" desc="Pronoun and register for second person.">
                            <SetSegmented
                                value={currentAddr}
                                onChange={(v) => updateAddress(activeLang, v)}
                                options={addrOpts}
                            />
                        </SetField>
                        <SetField label="Reading level" desc="Vocabulary and sentence complexity.">
                            <SetSelect
                                value={voice.reading || 'standard'}
                                onChange={(v) => update({ reading: v })}
                                minWidth={180}
                                options={[
                                    { value: 'simple', label: 'Plain & simple' },
                                    { value: 'standard', label: 'Standard' },
                                    { value: 'A1', label: 'CEFR A1 — Beginner' },
                                    { value: 'A2', label: 'CEFR A2 — Elementary' },
                                    { value: 'B1', label: 'CEFR B1 — Intermediate' },
                                    { value: 'B2', label: 'CEFR B2 — Upper-intermediate' },
                                    { value: 'C1', label: 'CEFR C1 — Advanced' },
                                    { value: 'C2', label: 'CEFR C2 — Proficient' },
                                    { value: 'technical', label: 'Expert / technical' },
                                ]}
                            />
                        </SetField>
                        <SetField label="Sentence length" desc="Preferred rhythm for the target copy.">
                            <SetSegmented
                                value={voice.length || 'balanced'}
                                onChange={(v) => update({ length: v })}
                                options={[
                                    { value: 'terse', label: 'Short' },
                                    { value: 'balanced', label: 'Balanced' },
                                    { value: 'expansive', label: 'Flowing' },
                                ]}
                            />
                        </SetField>
                    </SetBlock>
                </div>

                <SetBlock
                    title="Voice summary"
                    desc="One or two sentences describing how your brand sounds. Passed verbatim to the language model as a top-level brief — the most direct way to nudge style."
                >
                    <div className="bv-pad">
                        <textarea
                            value={voice.summary || ''}
                            onChange={(e) => update({ summary: e.target.value })}
                            placeholder="Describe how your brand should sound in one or two sentences…"
                            style={{
                                width: '100%', minHeight: 80,
                                border: '1px solid var(--line)', borderRadius: 10, padding: 12,
                                background: 'var(--bg)', color: 'var(--ink)',
                                fontFamily: 'inherit', fontSize: 13.5, outline: 'none', resize: 'vertical',
                            }}
                        />
                    </div>
                </SetBlock>

                <SetBlock
                    title="Do-not-translate"
                    desc="Names kept verbatim in every language — products, brands, and reserved terms. Honored by every engine, including DeepL and Google."
                >
                    <div className="bv-pad">
                        <ChipInput
                            items={voice.doNotTranslate || []}
                            placeholder="Add a term and press Enter…"
                            onAdd={(x) => update({ doNotTranslate: [...(voice.doNotTranslate || []), x] })}
                            onRemove={(x) => update({ doNotTranslate: (voice.doNotTranslate || []).filter((i) => i !== x) })}
                        />
                    </div>
                </SetBlock>

                <div className="bv-grid">
                    <SetBlock title="Avoid these" tag="Forbidden" flush
                        desc="Words the AI will never use, with the reason. Flagged on AI Quality if any engine slips one through."
                    >
                        {(voice.forbidden || []).map((f, i) => (
                            <RuleRow
                                key={i}
                                kind="ban"
                                primary={f.term || f.t || ''}
                                secondary={f.reason || f.r || ''}
                                onRemove={() => update({ forbidden: (voice.forbidden || []).filter((_, j) => j !== i) })}
                            />
                        ))}
                        <AddRuleInline
                            placeholders={['Term to avoid', 'Why / what to use instead']}
                            addLabel="Add forbidden term"
                            onAdd={(a, b) => update({ forbidden: [...(voice.forbidden || []), { term: a, reason: b }] })}
                        />
                    </SetBlock>

                    <SetBlock title="Prefer these" tag="Preferred" flush
                        desc="Approved phrasings the AI should reach for first. Whole-word, case-aware substitution applied after every draft."
                    >
                        {(voice.preferred || []).map((p, i) => (
                            <RuleRow
                                key={i}
                                kind="ok"
                                primary={(
                                    <>
                                        {p.avoid || p.a || ''}
                                        <span className="arr">→</span>
                                        {p.use || p.b || ''}
                                    </>
                                )}
                                onRemove={() => update({ preferred: (voice.preferred || []).filter((_, j) => j !== i) })}
                            />
                        ))}
                        <AddRuleInline
                            placeholders={['Avoid', 'Prefer']}
                            addLabel="Add preferred term"
                            onAdd={(a, b) => update({ preferred: [...(voice.preferred || []), { avoid: a, use: b }] })}
                        />
                    </SetBlock>
                </div>

                <SetBlock
                    title="Examples that train the AI"
                    desc="Show, don't tell. A few on-brand pairs teach the model your style faster than any rule."
                >
                    <ExamplesEditor
                        items={voice.examples || []}
                        onChange={(next) => update({ examples: next })}
                    />
                </SetBlock>

                <div className={`set-savebar ${dirty ? '' : 'saved'}`}>
                    <div className={`msg ${dirty ? '' : 'saved'}`}>
                        <span className="dot" />{dirty ? 'Unsaved voice changes' : 'Voice profile saved'}
                    </div>
                    <div className="grow" />
                    <button type="button" className="set-btn" onClick={discard} disabled={!dirty}>Discard</button>
                    <button type="button" className="set-btn primary" onClick={save} disabled={!dirty || saving}>
                        {saving ? 'Saving…' : 'Save changes'}
                    </button>
                </div>
            </div>
            </>
            )}
        </>
    );
};

export default BrandVoice;
